import { useMe, type AdminMe } from '../api/useMe.ts';
import type { Rom } from './rom.ts';

/**
 * Tilgangen panelet viser og låser etter (M4.8, «Rolle per arbeidsrom» i tech-decisions.md).
 *
 * Kilden er `AdminMe.access` — den EFFEKTIVE tilgangen per rom. `role` er det globale laget
 * (`GlobalRole`, eier eller medlem, M4.8 steg 4) og leses bare for å vite om brukeren er eier.
 *
 * Klienten er aldri autoriteten. Serveren vokter hver rute med `Require(rom, minimum)`; dette
 * er speilet som gjør at låsen forklares FØR et kall får 403 (`ux-flow-architect`, «Forklarte
 * låser»). Ukjent er strengest: før «meg» har svart kan ingen skrive noe sted.
 */
export interface Tilgang {
  /** «Meg» har svart. Før det er alt lukket, men `utenRom` er heller ikke sant. */
  kjent: boolean;
  eier: boolean;
  /** Rommene brukeren har en rolle i, i kontraktens rekkefølge. Skallets rom-bryter teller disse. */
  rom: Rom[];
  /** Et medlem uten noe rom: skallet har ingen sider (DD-01 presisert 2026-09-25). */
  utenRom: boolean;
  kanSe: (rom: Rom) => boolean;
  kanSkrive: (rom: Rom) => boolean;
  /** Rollen i rommet slik `access` gir den, eller `undefined` uten rad. Eier har behandler i hvert rom. */
  rolleI: (rom: Rom) => AdminMe['access'][number]['role'] | undefined;
  /**
   * Rollen i ord for «⋯» og «Du er …» — fra `access`, aldri fra `role`. Eier er «eier»; et
   * medlem med ett rom har rollen der («behandler», «leser»), og ordet for rommet trengs ikke;
   * med flere rom er det «medlem». Uten rom er det «medlem», og `utenRom` sier resten.
   */
  rolletekst: string | undefined;
}

/** Ren funksjon, så regelen kan testes uten et tre rundt seg. */
export function tilgangFra(me: AdminMe | undefined): Tilgang {
  const access = me?.access ?? [];
  const eier = me?.role === 'eier';
  const rolle = (rom: Rom) => access.find((a) => a.room === rom)?.role;
  return {
    kjent: me !== undefined,
    eier,
    rom: access.map((a) => a.room),
    utenRom: me !== undefined && !eier && access.length === 0,
    kanSe: (rom) => rolle(rom) !== undefined,
    kanSkrive: (rom) => rolle(rom) === 'behandler',
    rolleI: rolle,
    rolletekst: me === undefined ? undefined : eier ? 'eier' : access.length === 1 ? access[0]!.role : 'medlem',
  };
}

/** `kanSkrive(rom)` uten resten — det sidene i et rom spør om. */
export const kanSkrive = (me: AdminMe | undefined, rom: Rom): boolean => tilgangFra(me).kanSkrive(rom);

/** Tilgangen til den som er logget inn, fra `GET /v1/admin/me` (samme query som skallet). */
export function useTilgang(): Tilgang {
  return tilgangFra(useMe().data);
}
